import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import {
  labelOf,
  PAYMENT_METHOD_LABELS,
  PAYMENT_PROVIDER_LABELS,
} from '@/lib/enumLabels';
import type { AdminPaymentSort, PaymentSummary } from '@/lib/api-admin/types';
import { formatPaymentMinorAmount } from '@/features/orders/lib/chargeMoney';
import {
  PAYMENT_SORTABLE_COLUMNS,
  SORT_KEY_TO_COLUMN,
  togglePaymentSort,
} from '@/stores/usePaymentFiltersStore';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { PaymentStatusPill } from './PaymentStatusPill';

export interface PaymentsTableProps {
  items: ReadonlyArray<PaymentSummary>;
  sort: AdminPaymentSort;
  onSortChange: (sort: AdminPaymentSort) => void;
}

// [pay-currency] PaymentSummary.chargeAmount — be-snapshot additive 계약(Toss-native major
// decimal STRING, 미지원 통화는 null=키 생략). generated 타입 미반영이라 로컬 확장.
type PaymentSummaryExtended = PaymentSummary & { chargeAmount?: string | null };

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const PaymentsTable = ({ items, sort, onSortChange }: PaymentsTableProps) => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = SORT_KEY_TO_COLUMN[sort];

  const columns = useMemo<ColumnDef<PaymentSummary>[]>(
    () => [
      {
        id: 'createdAt',
        header: () => t('payments.columns.createdAt'),
        accessorFn: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'orderNo',
        header: () => t('payments.columns.orderNo'),
        cell: ({ row }) => (
          // QA-311: 독립몰 주문번호(ORD-1791864501098-8N3JF, 25자)가 잘리지 않게 한 줄 전체 표시.
          <span className="block min-w-[220px] whitespace-nowrap tabular-nums" title={row.original.orderNo ?? undefined}>
            {row.original.orderNo ?? '-'}
          </span>
        ),
      },
      {
        id: 'paymentMethod',
        header: () => t('payments.columns.paymentMethod'),
        accessorFn: (row) => labelOf(PAYMENT_METHOD_LABELS, row.paymentMethod),
      },
      {
        id: 'provider',
        header: () => t('payments.columns.provider'),
        accessorFn: (row) => labelOf(PAYMENT_PROVIDER_LABELS, row.provider),
      },
      {
        id: 'amount',
        header: () => t('payments.columns.amount'),
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {formatPaymentMinorAmount(
              row.original.amount,
              row.original.currencyCode,
              (row.original as PaymentSummaryExtended).chargeAmount,
            )}
          </span>
        ),
      },
      {
        id: 'refundedAmount',
        header: () => t('payments.columns.refundedAmount'),
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {row.original.refundedAmount != null
              ? formatPaymentMinorAmount(row.original.refundedAmount, row.original.currencyCode)
              : '-'}
          </span>
        ),
      },
      {
        id: 'displayStatus',
        header: () => t('payments.columns.status'),
        cell: ({ row }) => (
          <PaymentStatusPill
            displayStatus={row.original.displayStatus}
            paymentId={row.original.paymentId}
          />
        ),
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: items as PaymentSummary[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <p
        className="py-8 text-center text-sm text-[color:var(--content-muted)]"
        data-testid="payments-empty"
      >
        {t('payments.list.empty')}
      </p>
    );
  }

  return (
    <Table data-testid="payments-table">
      <TableHeader style={{ background: 'var(--surface-1)' }}>
        <TableRow>
          {table.getHeaderGroups()[0]?.headers.map((header) => {
            const isSortable = PAYMENT_SORTABLE_COLUMNS.has(header.column.id);
            const isActive = activeSortId === header.column.id;
            return (
              <TableHead key={header.id} scope="col">
                {isSortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1"
                    onClick={() => onSortChange(togglePaymentSort(sort, header.column.id))}
                    aria-label={t('payments.list.sortBy', {
                      column: t(`payments.columns.${header.column.id}`),
                    })}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span aria-hidden="true">
                      {isActive ? (activeSortDir === 'asc' ? '↑' : '↓') : ''}
                    </span>
                  </button>
                ) : (
                  flexRender(header.column.columnDef.header, header.getContext())
                )}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => {
          const payment = row.original;
          return (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              style={{ height: 'var(--density-compact-row-h)' }}
              onClick={() => navigate(`/payments/${payment.paymentId}`)}
              data-payment-id={payment.paymentId}
              data-testid="payments-row"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
